import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Question from '../../../views/ProductDetails/Series3CollarDetails/components/FAQ/Question';
import styles from './MiniFAQ.module.scss';
import { ReactComponent as IconHelp } from '../../../assets/images/icons/icon_help.svg';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { showNanoVariant } from '../../../components/NanoBanner/NanoBanner';
import { closeDecagonChat, openDecagonChat } from '../../../components/AppBar/NavBar';
import { series3CollarFAQChatButtonClick } from '../../../lib/analytics/events';
import { Species } from '../../../types/gql-op-types';

export default function FAQ({
  species,
  containerRef,
}: {
  species: Species;
  containerRef?: React.RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const decagonChatEnabled = useFeatureValue('decagon-chat-widget', false);
  const showDecagonChat = showNanoVariant() && decagonChatEnabled;

  useEffect(() => {
    const handleMessage = (event: any) => {
      if (event.origin !== 'https://decagon.ai') return;

      const { data } = event;
      if (data.type === 'DECAGON_INACTIVE') {
        closeDecagonChat();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      // Close the chat when navigating away
      closeDecagonChat();
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.container}>
      <h2 className={styles.heading}>{t('faq')}</h2>
      <div>
        <Question title={t('whats_the_weight_and_dimensions_of_the_fi_mini')} page="Mini" index={0}>
          {t('fi_mini_weight_and_dimensions_answer')}
        </Question>

        <Question title={t('what_types_of_collars_or_harnesses_does_the_fi_mini_fit_on')} page="Mini" index={1}>
          {t('fi_mini_collar_harness_fit_answer')}
          <ul>
            <li>{t('strap_minimum_none_maximum_1_25')}</li>
            <li>{t('soft_case_minimum_0_375_maximum_1_25')}</li>
            <li>{t('3_8_soft_case_minimum_0_375_maximum_0_375')}</li>
          </ul>
        </Question>

        <Question title={t('do_i_need_to_have_a_verizon_account_to_use_a_fi_mini')} page="Mini" index={2}>
          {t('verizon_account_not_required_answer')}
        </Question>

        <Question title={t('can_i_connect_my_pets_existing_fi_profile_to_a_new_fi_mini')} page="Mini" index={3}>
          {t('connect_existing_fi_profile_answer')}
        </Question>

        <Question title={t('why_is_fi_a_membership')} page="Mini" index={4}>
          {t('why_is_fi_a_membership_answer_part1')}
          <br />
          <br />
          {t('why_is_fi_a_membership_answer_part2')}
        </Question>

        <Question title={t('can_i_get_a_new_device_if_im_already_a_fi_member')} page="Mini" index={5}>
          {t('can_i_get_a_new_device_if_im_already_a_fi_member_answer')}
        </Question>

        <Question title={t('how_do_i_know_if_the_fi_mini_or_series_3_plus_is_right_for_me')} page="Mini" index={6}>
          {t('fi_mini_or_series_3_plus_right_for_me_answer')}
        </Question>
      </div>
      {showDecagonChat && (
        <div className={styles.decagonChatContainer}>
          <IconHelp />
          <div className={styles.decagonChatTitle}>{t('something_else_on_your_mind')}</div>
          <button
            className={styles.decagonChatButton}
            onClick={() => {
              series3CollarFAQChatButtonClick();
              openDecagonChat();
            }}
          >
            {t('chat_with_fi')}
          </button>
        </div>
      )}
    </div>
  );
}
